/**
 * Text clip video: video (MP4, YouTube, Vimeo) shows through bold text.
 *
 * Safari does NOT support mask-image: url(#svgId) on HTML elements — it only
 * works inside SVG. We use SVG data-URI masks instead, which work in all browsers.
 *
 * Use: <span class="text-bg-video-clip" data-video="https://...">coatings.</span>
 */

import { appendVimeoEmbed, createYoutubeEmbed } from "../utils/video-embeds.js";
import {
  applyDataUriMask,
  buildMaskDataUri,
  getLineSegments,
  whenHasLayout,
} from "../utils/text-mask-geometry.js";

const VIDEO_TYPE = {
  MP4: "mp4",
  YOUTUBE: "youtube",
  VIMEO: "vimeo",
};

const RESIZE_DEBOUNCE_MS = 150;

function getVideoType(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim().toLowerCase();
  if (u.endsWith(".mp4") || u.includes(".mp4?")) return VIDEO_TYPE.MP4;
  if (u.includes("youtube.com") || u.includes("youtu.be")) return VIDEO_TYPE.YOUTUBE;
  if (u.includes("vimeo.com")) return VIDEO_TYPE.VIMEO;
  return null;
}

function getYoutubeVideoId(url) {
  const m = url.trim().match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

function getVimeoVideoId(url) {
  const m = url.trim().match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

/** Recompute and re-apply the data-URI mask to the media wrapper (and video if present). */
function updateMask(span, text, media) {
  const w = media.clientWidth;
  const h = media.clientHeight;
  if (w <= 0 || h <= 0) return;

  const style = getComputedStyle(span);
  const fontSizePx = parseFloat(style.fontSize) || 16;
  const mediaRect = media.getBoundingClientRect();
  const textSpan = span.querySelector(".text-bg-video-clip__text");
  if (!textSpan) return;

  const segments = getLineSegments(textSpan, mediaRect, fontSizePx);
  if (!segments.length) return;

  const dataUri = buildMaskDataUri(segments, w, h, style);
  if (!dataUri) return;

  applyDataUriMask(media, dataUri);

  // For MP4, also apply to <video> itself: Safari sometimes ignores mask on the wrapper div
  const video = media.querySelector("video");
  if (video) applyDataUriMask(video, dataUri);
}

function scheduleMaskUpdate(span, text, media) {
  if (span._maskRafId != null) return;
  span._maskRafId = requestAnimationFrame(() => {
    span._maskRafId = null;
    updateMask(span, text, media);
  });
}

function addInlineTextSpan(span, text) {
  const textSpan = document.createElement("span");
  textSpan.className = "text-bg-video-clip__text";
  textSpan.textContent = text;
  span.appendChild(textSpan);
}

function markLoaded(span) {
  span.classList.add("text-bg-video-clip--loaded");
}

/** Run mask after fonts and layout are ready, then again after the next frame. */
function scheduleInitialMask(span, text, media) {
  const apply = () => updateMask(span, text, media);

  const run = () => {
    whenHasLayout(media, () => {
      apply();
      requestAnimationFrame(apply);
    });
  };

  if (document.fonts && typeof document.fonts.ready?.then === "function") {
    document.fonts.ready.then(run).catch(run);
  } else {
    run();
  }
}

function setupResizeObserver(span, text, media) {
  const ro = new ResizeObserver(() => scheduleMaskUpdate(span, text, media));
  ro.observe(span);
  ro.observe(media);

  let resizeTimeout;
  const onWindowResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => scheduleMaskUpdate(span, text, media), RESIZE_DEBOUNCE_MS);
  };
  window.addEventListener("resize", onWindowResize, { passive: true });

  return () => {
    ro.disconnect();
    window.removeEventListener("resize", onWindowResize);
    if (span._maskRafId != null) cancelAnimationFrame(span._maskRafId);
    clearTimeout(resizeTimeout);
  };
}

function initMp4Clip(span, videoUrl, text) {
  span.textContent = "";
  addInlineTextSpan(span, text);

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.loop = true;
  video.autoplay = true;
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.preload = "metadata";

  const media = document.createElement("div");
  media.className = "text-bg-video-clip__media";
  media.appendChild(video);
  span.appendChild(media);

  scheduleInitialMask(span, text, media);

  // Refresh mask on key video lifecycle events (WebKit may recomposite on these)
  const refresh = () => scheduleMaskUpdate(span, text, media);
  video.addEventListener("loadedmetadata", refresh, { once: true });
  video.addEventListener("playing", refresh, { once: true });
  video.addEventListener("canplaythrough", refresh, { once: true });
  video.addEventListener("canplay", () => markLoaded(span), { once: true });

  video.src = videoUrl;
  video.play().catch(() => {});

  return setupResizeObserver(span, text, media);
}

const IFRAME_EMBED_CLASS = "text-bg-video-clip__embed";

function initYoutubeClip(span, videoId, text) {
  span.textContent = "";
  addInlineTextSpan(span, text);

  const media = document.createElement("div");
  media.className = "text-bg-video-clip__media";
  span.appendChild(media);

  scheduleInitialMask(span, text, media);

  let resizeTeardown = () => {};

  createYoutubeEmbed(media, videoId, IFRAME_EMBED_CLASS).then(() => {
    scheduleMaskUpdate(span, text, media);
    setTimeout(() => markLoaded(span), 600);
    resizeTeardown = setupResizeObserver(span, text, media);
  });

  return () => {
    resizeTeardown();
  };
}

function initVimeoClip(span, videoId, text) {
  span.textContent = "";
  addInlineTextSpan(span, text);

  const media = document.createElement("div");
  media.className = "text-bg-video-clip__media";

  const iframe = appendVimeoEmbed(media, videoId, IFRAME_EMBED_CLASS);
  iframe.setAttribute("frameborder", "0");
  iframe.loading = "lazy";

  const REVEAL_DELAY_MS = 600;
  iframe.addEventListener(
    "load",
    () => {
      scheduleMaskUpdate(span, text, media);
      setTimeout(() => markLoaded(span), REVEAL_DELAY_MS);
    },
    { once: true }
  );

  span.appendChild(media);

  scheduleInitialMask(span, text, media);

  return setupResizeObserver(span, text, media);
}

export function textBgVideo() {
  const clips = document.querySelectorAll(".text-bg-video-clip[data-video]");
  const teardowns = [];

  clips.forEach((span) => {
    const url = span.getAttribute("data-video");
    const type = getVideoType(url);
    const text = span.textContent.trim();
    if (!type || !text) return;

    let teardown;
    if (type === VIDEO_TYPE.MP4) {
      teardown = initMp4Clip(span, url, text);
    } else if (type === VIDEO_TYPE.YOUTUBE) {
      const id = getYoutubeVideoId(url);
      if (id) teardown = initYoutubeClip(span, id, text);
    } else if (type === VIDEO_TYPE.VIMEO) {
      const id = getVimeoVideoId(url);
      if (id) teardown = initVimeoClip(span, id, text);
    }

    if (teardown) teardowns.push(teardown);
  });

  return () => teardowns.forEach((fn) => fn && fn());
}
